import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import DOMPurify from 'dompurify';
import { green, neutral } from '../../theme';
import { color } from '../../theme/tokens';

// ─── helpers ───────────────────────────────────────────────────────────────

const formatDate = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  } catch { return null; }
};

// ─── VerdictChips ──────────────────────────────────────────────────────────

function VerdictChips({ article }) {
  // Coerce defensively — DB may return string or null. Treat empty/whitespace
  // strings and non-finite values as "no score" (hide the chip), not 0.
  const rawScore = article.ai_study_strength?.score;
  const numericScore = typeof rawScore === 'number'
    ? rawScore
    : (typeof rawScore === 'string' && rawScore.trim() !== '' ? Number(rawScore) : NaN);
  const score = Number.isFinite(numericScore) ? numericScore : null;
  const isRct = article.ai_study_strength?.criteria?.is_rct;
  const nSubjects = article.ai_number_of_subjects;
  const studyType = article.ai_study_type;
  const confidence = article.ai_confidence_of_outcome;

  const chipSx = {
    height: '1.625rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    borderRadius: '0.5rem',
    '& .MuiChip-icon': { fontSize: '0.85rem' },
    '& .MuiChip-label': { px: '0.5rem' },
  };

  const metaChips = [
    isRct && (
      <Chip
        key="rct"
        icon={<VerifiedOutlinedIcon />}
        label="RCT"
        size="small"
        sx={{ ...chipSx, backgroundColor: 'rgba(46,125,50,0.08)', color: green.brand, border: `0.0625rem solid rgba(46,125,50,0.25)` }}
      />
    ),
    nSubjects != null && (
      <Chip
        key="n"
        icon={<PeopleOutlineIcon />}
        label={`n=${nSubjects}`}
        size="small"
        sx={{ ...chipSx, backgroundColor: neutral[100], color: neutral[700], border: `0.0625rem solid ${neutral[300]}` }}
      />
    ),
    studyType && studyType.length < 40 && !/\brct\b/i.test(studyType) && (
      <Chip
        key="type"
        icon={<ScienceOutlinedIcon />}
        label={studyType}
        size="small"
        sx={{ ...chipSx, backgroundColor: neutral[100], color: neutral[700], border: `0.0625rem solid ${neutral[300]}` }}
      />
    ),
    confidence && confidence.length < 30 && !/[<>=;]/.test(confidence) && (
      <Chip
        key="conf"
        label={confidence}
        size="small"
        sx={{ ...chipSx, backgroundColor: neutral[100], color: neutral[700], border: `0.0625rem solid ${neutral[300]}` }}
      />
    ),
  ].filter(Boolean);

  const strengthChip = score != null && (
    <Tooltip title="Study strength scores studies from 0 (weak) to 5 (strong). Higher scores indicate well-designed trials with significant results.">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          px: '0.75rem',
          py: '0.375rem',
          border: '0.0625rem solid rgba(46,125,50,0.2)',
          borderRadius: '0.5rem',
          backgroundColor: 'rgba(46,125,50,0.04)',
          cursor: 'default',
          width: { xs: '100%', md: 'auto' },
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: neutral[500], textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Study Strength
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.1875rem', flexShrink: 0 }}>
          {[...Array(5)].map((_, i) => (
            <Box key={i} sx={{
              width: '1rem',
              height: '0.25rem',
              borderRadius: '0.125rem',
              backgroundColor: i < score ? green.brand : 'rgba(46,125,50,0.12)',
              opacity: i < score ? 0.7 + i * 0.06 : 0.15,
            }} />
          ))}
        </Box>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: green.brand, flexShrink: 0 }}>
          {score.toFixed(1)}
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <Box sx={{ mt: '1.25rem' }}>
      {/* Desktop: all on one row. Mobile: strength on own row, meta chips below */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        {strengthChip}
        {metaChips}
      </Box>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: '0.5rem' }}>
        {score != null && <Box>{strengthChip}</Box>}
        {metaChips.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>{metaChips}</Box>}
      </Box>
    </Box>
  );
}

// ─── StudyMeta (collapsible) ───────────────────────────────────────────────

function StudyMeta({ article }) {
  const date = formatDate(article.date_published);
  const hasAny = date || article.journal_title || article.first_author_name ||
    article.first_author_affiliation || article.ai_subject_condition ||
    article.ai_study_country || article.link_to_full_text;

  if (!hasAny) return null;

  const row = (label, value) => value ? (
    <Box key={label} sx={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
      <Typography
        component="span"
        sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: neutral[500],
          whiteSpace: 'nowrap',
          minWidth: '5rem',
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography component="span" sx={{ fontSize: '0.82rem', color: neutral[700], lineHeight: 1.5 }}>
        {value}
      </Typography>
    </Box>
  ) : null;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mt: '1.5rem',
        backgroundColor: 'transparent',
        border: `0.0625rem solid ${neutral[300]}`,
        borderRadius: '0.75rem !important',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { mt: '1.5rem' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.1rem', color: neutral[500] }} />}
        sx={{
          px: '1rem',
          minHeight: 'unset',
          py: '0.625rem',
          '& .MuiAccordionSummary-content': { my: 0 },
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: neutral[500], textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Publication details
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: '1rem', pt: 0, pb: '1rem' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {row('Author', article.first_author_name)}
          {row('Affiliation', article.first_author_affiliation)}
          {row('Condition', article.ai_subject_condition)}
          {row('Country', article.ai_study_country)}
        </Box>
        {article.link_to_full_text && (
          <Button
            href={article.link_to_full_text}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            startIcon={<LinkIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              mt: '0.875rem',
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 600,
              color: green.brand,
              px: '0.625rem',
              py: '0.25rem',
              borderRadius: '0.5rem',
              border: `0.0625rem solid rgba(46,125,50,0.3)`,
              '&:hover': { backgroundColor: 'rgba(46,125,50,0.06)' },
            }}
          >
            View full paper
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ─── ArticleHero ──────────────────────────────────────────────────────────

export default function ArticleHero({ article, supplementKey, conditionKey }) {
  const label = supplementKey
    ? supplementKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : conditionKey
      ? conditionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : null;

  return (
    <Box
      sx={{
        background: `linear-gradient(150deg, ${green.tintSm} 0%, ${color.paper} 55%)`,
        border: `0.0625rem solid ${green.tintMd}`,
        borderRadius: '1.25rem',
        boxShadow: `0 0.5rem 2rem ${green.shadow}`,
        p: { xs: '1.25rem', sm: '1.75rem', md: '2.5rem' },
        mb: { xs: '1.5rem', sm: '2rem' },
      }}
    >
      {/* Overline */}
      {label && (
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: green.brand,
            mb: '0.625rem',
          }}
        >
          Research · {label}
        </Typography>
      )}

      {/* Plain-language finding — the H1 */}
      {article.ai_title_simplified ? (
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: '1.35rem', sm: '1.6rem', md: '2rem' },
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: '-0.02em',
            color: neutral[900],
            wordBreak: 'break-word',
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.ai_title_simplified) }}
        />
      ) : (
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.3rem' },
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: '-0.015em',
            color: neutral[900],
            wordBreak: 'break-word',
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.title) }}
        />
      )}

      {/* Original title as muted caption when plain-language version exists */}
      {article.ai_title_simplified && article.title && (
        <Typography
          sx={{
            mt: '0.5rem',
            fontSize: '0.78rem',
            color: neutral[500],
            fontStyle: 'italic',
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.title) }}
        />
      )}

      {/* Journal + year — attribution for the title */}
      {(article.journal_title || article.date_published) && (
        <Typography
          sx={{
            mt: '0.5rem',
            fontSize: '0.75rem',
            color: neutral[500],
            lineHeight: 1.4,
          }}
        >
          {[article.journal_title, formatDate(article.date_published)]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      )}

      {/* Verdict chips */}
      <VerdictChips article={article} />

      {/* Collapsible publication details (author, affiliation, country, full paper) */}
      <StudyMeta article={article} />
    </Box>
  );
}
